import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Sparkles, MonitorSmartphone, Repeat, Layers, Check, Clapperboard, ImageIcon, Wand2 } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal, Eyebrow } from "@/components/Reveal";

const APPROACH = ["Idea", "Product Development", "Web / App Platform", "User Acquisition", "Subscription", "Continuous Development"];

const WHY = [
    { icon: Sparkles, title: "AI-First Products", text: "Building products around emerging artificial intelligence technologies." },
    { icon: MonitorSmartphone, title: "Digital Platforms", text: "Developing scalable web and application-based experiences." },
    { icon: Repeat, title: "Subscription Business", text: "Building recurring digital revenue through subscription-based products." },
    { icon: Layers, title: "Multi-Product Vision", text: "Developing multiple proprietary applications over time." },
];

const BENEFITS = [
    "Faster creative experimentation",
    "AI-assisted image and video creation",
    "Useful for content production",
    "Useful for marketing campaigns",
    "Useful for concept development",
    "Useful for filmmakers and pre-production",
    "Useful for social media content",
    "Useful for digital storytelling",
    "Accessible through a modern web platform",
    "Subscription-based access to premium capabilities",
];

export default function Home() {
    return (
        <div data-testid="home-page">
            <SEO
                title="ANA International Private Limited | AI & Digital Technology Company"
                description="ANA International Private Limited builds AI-powered applications and digital platforms. Explore Lyanka AI, our AI-powered image and video creation platform for creators, filmmakers, marketers and digital storytellers."
                path="/"
            />

            {/* HERO */}
            <section className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16">
                <div className="absolute inset-0 grid-bg" />
                <div className="absolute top-[-20%] right-[-10%] w-[700px] h-[700px] glow-gold rounded-full" />
                <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10 grid lg:grid-cols-2 gap-16 items-center w-full">
                    <div>
                        <Reveal>
                            <Eyebrow>AI Technology Company — India</Eyebrow>
                            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-6" data-testid="hero-headline">
                                Building the Future of <span className="gold-text">Digital Products.</span>
                            </h1>
                            <p className="text-[#a3a29b] text-base sm:text-lg leading-relaxed mt-7 max-w-xl" data-testid="hero-subheadline">
                                ANA INTERNATIONAL PRIVATE LIMITED develops AI-powered applications and digital platforms
                                designed to transform creativity, entertainment and everyday digital experiences.
                            </p>
                            <div className="flex flex-wrap gap-4 mt-10">
                                <Link to="/lyanka-ai" className="btn-gold" data-testid="hero-cta-lyanka">
                                    Explore Lyanka AI <ArrowRight size={16} />
                                </Link>
                                <Link to="/vision" className="btn-line" data-testid="hero-cta-vision">
                                    Discover Our Vision
                                </Link>
                            </div>
                            <div className="flex flex-wrap gap-x-8 gap-y-3 mt-12 text-xs text-[#7c7b75] font-mono tracking-wider">
                                <span>INCORPORATED · 06 DEC 2025</span>
                                <span>CIN · U46301PN2025PTC248911</span>
                            </div>
                        </Reveal>
                    </div>

                    {/* Hero visual — flagship product composition */}
                    <Reveal delay={0.15} className="relative hidden lg:block">
                        <div className="relative h-[520px]" data-testid="hero-product-visual">
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-[440px] h-[440px] rounded-full border border-white/[0.07] orbit-spin">
                                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#c9a227]" />
                                </div>
                            </div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-[320px] h-[320px] rounded-full border border-[#c9a227]/20" />
                            </div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="glass rounded-3xl p-8 w-[280px] floaty">
                                    <img src="/assets/lyanka-mark.png" alt="Lyanka AI — AI image and video creation platform" className="rounded-2xl w-full" />
                                    <p className="mono-label mt-5 !text-[10px]">Flagship Product</p>
                                    <p className="font-display font-semibold mt-1 text-sm text-[#f5f4f0]">AI Image & Video Creation</p>
                                </div>
                            </div>
                            <div className="absolute top-8 left-2 glass rounded-2xl px-4 py-3 floaty" style={{ animationDelay: "-2s" }}>
                                <div className="flex items-center gap-2.5">
                                    <ImageIcon size={16} className="text-[#e8c766]" />
                                    <span className="text-xs text-[#c9c8c2]">AI Image Generation</span>
                                </div>
                            </div>
                            <div className="absolute bottom-16 right-0 glass rounded-2xl px-4 py-3 floaty" style={{ animationDelay: "-4s" }}>
                                <div className="flex items-center gap-2.5">
                                    <Clapperboard size={16} className="text-[#e8c766]" />
                                    <span className="text-xs text-[#c9c8c2]">AI Video Generation</span>
                                </div>
                            </div>
                            <div className="absolute bottom-4 left-10 glass rounded-2xl px-4 py-3 floaty" style={{ animationDelay: "-1s" }}>
                                <div className="flex items-center gap-2.5">
                                    <Wand2 size={16} className="text-[#e8c766]" />
                                    <span className="text-xs text-[#c9c8c2]">Creative Tools</span>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* APPROACH */}
            <section className="border-y border-white/[0.07] bg-[#0d0d0f]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
                    <Eyebrow>How We Build</Eyebrow>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-4 mt-7" data-testid="approach-flow">
                        {APPROACH.map((step, i) => (
                            <div key={step} className="flex items-center gap-4">
                                <span className="font-display text-sm sm:text-base font-semibold text-[#f5f4f0] tracking-wide uppercase">
                                    {step}
                                </span>
                                {i < APPROACH.length - 1 && <ArrowRight size={15} className="text-[#c9a227]" />}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FIRST PRODUCT */}
            <section className="py-28">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
                    <Reveal>
                        <Eyebrow>Our First Product</Eyebrow>
                        <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl mt-5 leading-tight">
                            LYANKA <span className="gold-text">AI</span>
                        </h2>
                        <p className="text-[#a3a29b] text-base sm:text-lg leading-relaxed mt-6 max-w-lg">
                            An AI-powered creative platform for generating images and videos — built for creators,
                            filmmakers, editors, marketers and digital storytellers.
                        </p>
                        <Link to="/lyanka-ai" className="btn-gold mt-9" data-testid="product-cta-lyanka">
                            Explore Lyanka AI <ArrowUpRight size={16} />
                        </Link>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <div className="card-line rounded-3xl p-10 flex flex-col items-center" data-testid="product-card-lyanka">
                            <img src="/assets/lyanka-mark.png" alt="Lyanka AI platform logo" className="w-56 rounded-2xl" />
                            <div className="flex gap-2 mt-8 flex-wrap justify-center">
                                {["AI Images", "AI Video", "Creative Visuals", "Storytelling"].map((t) => (
                                    <span key={t} className="text-[11px] font-mono tracking-widest uppercase text-[#c9c8c2] border border-white/10 rounded-full px-3.5 py-1.5">
                                        {t}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* WHY ANA */}
            <section className="py-24 bg-[#0d0d0f] border-y border-white/[0.07]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <Reveal>
                        <Eyebrow>Why ANA International</Eyebrow>
                        <h2 className="font-display font-bold text-3xl sm:text-4xl mt-5 max-w-2xl leading-tight">
                            A technology product company, built for the long term.
                        </h2>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14" data-testid="why-ana-cards">
                        {WHY.map((w, i) => (
                            <Reveal key={w.title} delay={i * 0.08}>
                                <div className="card-line rounded-2xl p-7 h-full">
                                    <w.icon size={22} className="text-[#e8c766]" />
                                    <h3 className="font-display font-semibold text-base mt-5 uppercase tracking-wide">{w.title}</h3>
                                    <p className="text-sm text-[#a3a29b] leading-relaxed mt-3">{w.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* TURN IDEAS INTO VISUALS */}
            <section className="py-28">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
                    <Reveal>
                        <div className="lg:sticky lg:top-32">
                            <Eyebrow>Lyanka AI</Eyebrow>
                            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl mt-5 leading-tight">
                                Turn Ideas Into <span className="gold-text">Visuals.</span>
                            </h2>
                            <p className="text-[#a3a29b] text-base sm:text-lg leading-relaxed mt-6">
                                Traditional visual production can require significant time, technical expertise, software
                                and resources. Lyanka AI is designed to make AI-assisted visual creation more accessible
                                by providing creative generation tools through one digital platform.
                            </p>
                            <Link to="/lyanka-ai" className="btn-line mt-9" data-testid="visuals-cta-lyanka">
                                See how it works <ArrowRight size={16} />
                            </Link>
                        </div>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 gap-4" data-testid="benefits-grid">
                        {BENEFITS.map((b, i) => (
                            <Reveal key={b} delay={i * 0.05}>
                                <div className="flex items-start gap-3 card-line rounded-xl px-5 py-4">
                                    <Check size={16} className="text-[#c9a227] mt-0.5 shrink-0" />
                                    <span className="text-sm text-[#d8d7d1]">{b}</span>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* SUBSCRIPTION MODEL */}
            <section className="py-24 bg-[#0d0d0f] border-y border-white/[0.07] relative overflow-hidden">
                <div className="absolute bottom-[-40%] left-[-10%] w-[600px] h-[600px] glow-gold rounded-full" />
                <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
                    <Reveal>
                        <Eyebrow>Business Model</Eyebrow>
                        <h2 className="font-display font-bold text-3xl sm:text-4xl mt-5">Built on a Subscription Model</h2>
                        <p className="text-[#a3a29b] text-base sm:text-lg leading-relaxed mt-6 max-w-3xl">
                            Lyanka AI operates on a subscription-based digital business model. Users subscribe to access
                            premium platform capabilities and allocated usage according to their selected plan. The model
                            allows ANA INTERNATIONAL to continuously invest in AI technology, product development,
                            infrastructure, new creative tools, platform improvements, user experience, and research
                            and development.
                        </p>
                        <Link to="/lyanka-ai" className="btn-gold mt-9" data-testid="subscription-cta">
                            View Lyanka AI <ArrowRight size={16} />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* VISION CTA */}
            <section className="py-28">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
                    <Reveal>
                        <Eyebrow>Our Vision</Eyebrow>
                        <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl mt-5 max-w-3xl mx-auto leading-tight">
                            Building the Next Generation of <span className="gold-text">Digital Products.</span>
                        </h2>
                        <p className="text-[#a3a29b] text-base sm:text-lg leading-relaxed mt-6 max-w-2xl mx-auto">
                            A portfolio of proprietary technology products across artificial intelligence, creativity
                            and digital entertainment — starting with Lyanka AI.
                        </p>
                        <Link to="/vision" className="btn-line mt-9" data-testid="vision-cta">
                            Discover Our Vision <ArrowRight size={16} />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
